export const OFFLINE_MODE_ACTIVATION_PERMISSION = 'offline_mode_activation';

export const OFFLINE_MODE_ADMIN_ROLE = 'offline_mode_admin';

export const ORG_ADMIN_ROLE = 'admin';

export const SUPER_ADMIN_PRODUCT_ROLE = 'tt_super_admin';

export type LocalConsoleAccessGrant =
  | 'offline-mode-admin'
  | 'super-admin'
  | 'org-admin-grandfathered'
  | 'none';

export type LocalConsoleAccess = {
  canOpenConsole: boolean;
  canAdministerOfflineMode: boolean;
  grantedBy: LocalConsoleAccessGrant;
  isGrandfathered: boolean;
  isResolved: boolean;
};

export type ResolveLocalConsoleAccessInput = {
  organizationRoles?: readonly string[] | null;
  productRoles?: readonly string[] | null;
};

const includesRole = (roles: readonly string[] | null | undefined, role: string): boolean =>
  Array.isArray(roles) && roles.includes(role);

export function resolveLocalConsoleAccess({
  organizationRoles,
  productRoles,
}: ResolveLocalConsoleAccessInput): LocalConsoleAccess {
  const isSuperAdmin = includesRole(productRoles, SUPER_ADMIN_PRODUCT_ROLE);
  const holdsActivationPermission = includesRole(organizationRoles, OFFLINE_MODE_ADMIN_ROLE);
  const isOrgAdmin = includesRole(organizationRoles, ORG_ADMIN_ROLE);

  let grantedBy: LocalConsoleAccessGrant = 'none';
  if (holdsActivationPermission) {
    grantedBy = 'offline-mode-admin';
  } else if (isSuperAdmin) {
    grantedBy = 'super-admin';
  } else if (isOrgAdmin) {
    grantedBy = 'org-admin-grandfathered';
  }

  const allowed = grantedBy !== 'none';

  return {
    canOpenConsole: allowed,
    canAdministerOfflineMode: allowed,
    grantedBy,
    isGrandfathered: grantedBy === 'org-admin-grandfathered',
    isResolved: Array.isArray(organizationRoles) || Array.isArray(productRoles),
  };
}

export const NO_LOCAL_CONSOLE_ACCESS: LocalConsoleAccess = Object.freeze({
  canOpenConsole: false,
  canAdministerOfflineMode: false,
  grantedBy: 'none',
  isGrandfathered: false,
  isResolved: false,
});

export default resolveLocalConsoleAccess;
